'use strict';
import moment from 'moment';

const viewNames = {
	timeline: {
		day: 'timelineDay',
		week: 'timelineWeek',
		month: 'timelineMonth',
	},
	agenda: {
		day: 'agendaDay',
		week: 'agendaWeek',
		month: 'month',
	},
};
const defaultCalendarView = 'week';
const unassignedId = 'unassigned';

export default class CalendarController {
	constructor($scope, $q, $timeout, $ocLazyLoad, lazyLoadCfg, $element, $rootScope, $compile, i18n, holidaysService, personalizationService, colorService, themeService) {
		this.$scope = $scope;
		this.$q = $q;
		this.$timeout = $timeout;
		this.$ocLazyLoad = $ocLazyLoad;
		this.lazyLoadCfg = lazyLoadCfg;
		this.$element = $element;
		this.$rootScope = $rootScope;
		this.$compile = $compile;
		this.i18n = i18n;
		this.holidaysService = holidaysService;
		this.personalizationService = personalizationService;
		this.colorService = colorService;
		this.themeService = themeService;

		this.isAbstractMode = false;
		this.isShowHolidays = false;
		this.isGroupingMode = false;
		this.resourcesRendered = {};

		this.unassignedTitle = i18n.get('solutionBuilderAgent.components.mx-calendar.unassigned');

		this.events = [];
		this.resources = [];
		this._resourcePromise = $q.when(this.resources);
		this.firstLoad = true;
		this.calendarView = defaultCalendarView;
		this.calendarWidth = 'auto';

		this.options = {
			schedulerLicenseKey: '0346366776-fcs-1564564612',
			header: {
				left: 'title',
				center: '',
			},
			columnHeaderHtml: function (mom) {
				return (
					'<div class="calendar-column-header">' +
					'<div class="calendar-day-name long">' +
					mom.format('dddd') +
					'</div>' +
					'<div class="calendar-day-name short">' +
					mom.format('ddd') +
					'</div>' +
					'<div class="calendar-date">' +
					mom.format('DD/MM') +
					'</div>' +
					'</div>'
				);
			},

			eventSources: [
				{
					id: 'items',
					events: this.loadEvents.bind(this),
				},
				{
					id: 'holidays',
					events: async (start, end, timezone, callback) => {
						if (!this.isShowHolidays || this.isAbstractMode) {
							callback([]);
							return;
						}
						const holidays = await this.loadHolidays(start, end);
						callback(holidays);
					},
				},
			],
			loading: (isLoading) => {
				this.loading = isLoading;
			},
			resourceLabelText: i18n.get('solutionBuilderAgent.components.mx-calendar.resources'),
			handleWindowResize: true,
			height: 'parent',
			eventLimit: true,
			// defaultAllDayEventDuration: {days: 1},
			eventRender: this.eventRender.bind(this),
			viewRender: this.viewRender.bind(this),
			eventDestroy: this.onEventDestroy.bind(this),
			eventClick: this.onEventClick.bind(this),
			timezone: 'local',
			unselectAuto: false,
			windowResize: () => {
				if (typeof this.reset === 'function') {
					this.reset();
					delete this.reset;
				}

				if (!this.autoWidth) {
					//in case if calendar in 'fixed' mode (100% width of parent)
					//columns can be too small to show full day name
					const width = this.calendarContainerEl.width();

					if (width < 700 && !this.calendarContainerEl.hasClass('short-day-name')) {
						this.calendarContainerEl.addClass('short-day-name');
					} else if (width > 700 && this.calendarContainerEl.hasClass('short-day-name')) {
						this.calendarContainerEl.removeClass('short-day-name');
					}
				}
			},
			snapDuration: '00:05:00',
			eventAfterAllRender: this.onAfterAllRendered.bind(this),
			eventTimeFormat: moment.localeData().longDateFormat('LT').replace(/\s*a$/i, 'a'), // format like '4:00pm' instead of `4p`
		};
		this.initEventColor();
		$q.all([
			import(/* webpackChunkName: "calendar" */ 'fullcalendar/dist/fullcalendar.min.css'),
			import(/* webpackChunkName: "calendar" */ 'fullcalendar/dist/fullcalendar.js'),
			import(/* webpackChunkName: "calendar" */ 'fullcalendar/dist/locale-all.js'),
			import(/* webpackChunkName: "calendar" */ 'fullcalendar-scheduler/dist/scheduler.min.js'),
			import(/* webpackChunkName: "calendar" */ 'fullcalendar-scheduler/dist/scheduler.min.css'),
			import(/* webpackChunkName: "calendar" */ 'angular-ui-calendar/src/calendar.js'),
		]).then(() => {
			$ocLazyLoad.inject('ui.calendar');
			this.lazyLoaded = true;
		});

		$scope.$watch('vm.language', (newLang) => {
			if (newLang && newLang !== this.options.lang) {
				this.options.lang = newLang;
			}
		});

		$scope.$watch('vm.businessHours', (businessHours) => {
			if (businessHours !== undefined && this.calendar) {
				this.calendar.option('businessHours', businessHours);
			}
		});
	}

	$onInit() {
		let allowEventsOverlapping = false;

		enableJQueryHtmlPrefilterHack();

		this.isAbstractMode = (this.abstractMode || '').toLowerCase() === 'true'; // default: false
		this.isShowHolidays = (this.showHolidays || '').toLowerCase() === 'true'; // default: false
		allowEventsOverlapping = (this.eventOverlap || '').toLowerCase() !== 'false'; // default: true
		this.isGroupingMode = typeof this.itemExtraDimensionKey === 'string' && this.itemExtraDimensionKey.length > 0;
		this.calendarContainerEl = this.$element.find('.calendar-container');

		const minHeight = parseInt(this.height, 10);
		if (!isNaN(minHeight)) {
			this.$element.css({ height: minHeight + 'px' });
		}

		this.itemTitleKey = this.itemTitleKey || 'title';
		this.itemDescriptionKey = this.itemDescriptionKey || 'description';
		this.itemStartKey = this.itemStartKey || 'start';
		this.itemEndKey = this.itemEndKey || 'end';

		// Calendar options
		angular.extend(this.options, {
			defaultView: this.getDefaultView(),
			eventOverlap: allowEventsOverlapping,
			businessHours: this.businessHours || false,
			buttonText: {
				today: this.getTodayButtonTitle(),
			},
		});

		this.updateRangeButtons();

		if (this.isAbstractMode) {
			this.enableAbstractMode();
		}

		if (this.isGroupingMode) {
			this.enableGroupingMode();
		}

		if (this.isTimeline) {
			this.enableTimeline();
		}

		if (!this.language) {
			this.language = this.i18n.language;
		}

		if (this.language) {
			this.options.lang = this.language;
			this.options.locale = moment.locale();
		}

		if ((this.enableAdding || '').toLowerCase() === 'true') {
			this.options.selectable = true;
			this.options.select = (start, end, event, view, resource) => {
				const newItem = {};

				// convert to local time for 'all-day' events, so time will be set as 00:00 for local time-zone
				newItem[this.itemStartKey] = this.getLocalTimeISOString(start);
				newItem[this.itemEndKey] = this.getLocalTimeISOString(end);

				if (this.itemWholeDayKey) {
					newItem[this.itemWholeDayKey] = !start.hasTime();
				}

				if (this.isGroupingMode) {
					newItem[this.itemExtraDimensionKey] = resource.dimensionId;
				}
				if (
					allowEventsOverlapping ||
					//TODO: resource must be used in overlap calculation
					(!allowEventsOverlapping && !this._doesEventOverlapAnyEvents(newItem))
				) {
					this.createEvent(newItem);
				}
			};
		}

		if ((this.enableChanging || '').toLowerCase() === 'true') {
			this.options.editable = true;
			// Drag & drop
			this.options.eventDrop = this._handleItemPositionChange.bind(this);
			this.options.eventResize = this._handleItemPositionChange.bind(this);
		}
	}

	$onChanges(changes) {
		const reloadResourcesIsNeeded =
			(changes.extraDimensions && Array.isArray(changes.extraDimensions.currentValue)) ||
			(changes.showUnassigned && typeof changes.showUnassigned.currentValue === 'boolean');

		if (reloadResourcesIsNeeded && !this.waitingDimensions) {
			this.reloadResources();
		}

		if (changes.isTimeline && typeof changes.isTimeline.currentValue === 'boolean') {
			if (changes.isTimeline.currentValue) {
				this.enableTimeline();
			} else {
				this.disableTimeline();
			}
		}

		if (changes.calendarView && typeof changes.calendarView.currentValue === 'string') {
			this.setCalendarView(changes.calendarView.currentValue);
		}
	}

	$onDestroy() {
		disableJQueryHtmlPrefilterHack();
	}

	async loadEvents(start, end, timezone, callback) {
		const defer = this.$q.defer();
		this._resourcePromise = defer.promise;
		await this.loadItems(start, end);
		this.events = this.mapEvents(this.items, start, end);
		this.waitingDimensions = true;

		this.$timeout(() => {
			//wait 1 digest while extra-dimensions will be bind to directive, to avoid double loading
			this.waitingDimensions = false;

			this.previousRangeStart = start.clone();
			this.previousRangeEnd = end.clone();

			const resources = this.getResources(start, end);
			const resourcesChanged = this.isResourcesChanged(this.resources, resources);
			if (resourcesChanged) {
				this.resources = resources;
			}
			defer.resolve(this.resources);
			callback(this.events);
		});
	}

	async loadItems(start, end) {
		start = start.clone();
		end = end.clone();
		if (!this.itemsWatcher) {
			const watcherDefer = this.$q.defer();
			this.itemsWatcher = this.$scope.$watchCollection('vm.items', (newValue, oldValue) => {
				watcherDefer.resolve();
				if (newValue === oldValue) {
					return;
				}
				if (!this.internalRequestInProgress) {
					if (this.calendar) {
						this.itemsWatcherInProgress = true;
						this.calendar.refetchEvents();
						this.itemsWatcherInProgress = false;
					}
				}
			});
			await watcherDefer.promise;
		}

		if (typeof this.itemsLoader === 'function' && !this.itemsWatcherInProgress) {
			this.addBufferTime(start, end);
			this.internalRequestInProgress = true;
			this.items = await this.itemsLoader({
				start: start,
				end: end,
				currentDate: this.calendar ? this.calendar.currentDate : moment(),
				calendarView: this.calendarView,
			});
			this.internalRequestInProgress = false;
		}
		return this.$q.when(this.items);
	}

	getWholeDayTimezoneCorrection(_date) {
		//wholeDay events has Start as time and not as date (because there is no Date type in DB),
		//and start is saved 00:00 in local time (ex. for Ukraine it is saved as 21:00 previous day UTC)
		//so it is necessary to get just correct Date from such time with correction on time-zone
		const date = moment.utc(_date);

		if (date.hour() >= 12) {
			date.add(1, 'days');
		}
		date.stripTime();

		return date.toISOString();
	}

	getLocalTimeISOString(_date) {
		return moment(_date.toISOString()).toISOString();
	}

	mapEvent(item) {
		const color = (item[this.itemColorKey] || '').toLowerCase().replace('0xff', '#');
		const allDay = Boolean(item[this.itemWholeDayKey]);
		let start = item[this.itemStartKey];
		let end = item[this.itemEndKey];

		if (allDay) {
			start = this.getWholeDayTimezoneCorrection(start);
			const endOfOneDayAllDay = moment(start).add(1, 'd').stripTime().toISOString();

			if (end) {
				end = this.getWholeDayTimezoneCorrection(end);
				if (moment(end).isSameOrBefore(moment(start))) {
					end = endOfOneDayAllDay;
				}
			} else {
				end = endOfOneDayAllDay;
			}
		}

		const event = {
			id: item._id,
			backgroundColor: color,
			borderColor: color,
			textColor: this.colorService.getBrightness(color) > 123 ? '#000000cc' : '#ffffffcc',
			allDay: allDay,
			resourceId: this.getResourceIdFromItem(item),
			dimensionId: this.getExtraDimensionIdFromItem(item),
			start: start,
			end: end,

			__originalItem: item,
		};

		if (item[this.itemBackgroundKey]) {
			angular.extend(event, {
				editable: false,
				isBackgroundItem: true,
				rendering: 'background',
			});
		} else {
			angular.extend(event, {
				title: item[this.itemTitleKey],
				description: item[this.itemDescriptionKey],
				editable: this.options.editable && !item[this.itemReadonlyKey],
				className: item[this.itemReadonlyKey] ? ['readonly'] : [],
			});
		}

		return event;
	}

	mapEvents(data, start, end) {
		const comparer = this.rangeComparer(start, end);

		if (Array.isArray(data)) {
			return this.mapComplexEvents(data.map(this.mapEvent, this))
				.filter(comparer, this)
				.filter((event) => {
					return !this.isTimeline || !event.isBackgroundItem;
				});
		}
		return [];
	}

	rangeComparer(_periodStart, _periodEnd) {
		const allDayPeriodStart = moment.utc(this.getWholeDayTimezoneCorrection(_periodStart));
		const allDayPeriodEnd = moment.utc(this.getWholeDayTimezoneCorrection(_periodEnd));

		return function isDateInRange(event) {
			const periodStart = event.allDay ? allDayPeriodStart : _periodStart;
			const periodEnd = event.allDay ? allDayPeriodEnd : _periodEnd;

			const eventStart = moment.utc(event.start);
			const eventEnd = moment.utc(event.end);

			if (event.allDay) {
				eventStart.stripTime();
				eventEnd.stripTime();
			}

			const diff = Math.min(periodEnd, eventEnd) - Math.max(periodStart, eventStart);
			return diff > 0 || (event.allDay && diff > 0);
		};
	}

	mapComplexEvents(events) {
		const eventsGroupedById = {};

		events.sort(function (eventA, eventB) {
			const diff = moment(eventA.start) - moment(eventB.start);
			return diff > 0 ? 1 : diff ? 0 : -1;
		});
		events.forEach(function (event) {
			if (typeof event.id === 'undefined') {
				return;
			}
			eventsGroupedById[event.id] = eventsGroupedById[event.id] || [];
			eventsGroupedById[event.id].push(event);
		});

		Object.keys(eventsGroupedById).forEach((id) => {
			const groupedEvents = eventsGroupedById[id];
			if (groupedEvents.length > 1) {
				const complexEventConfig = {
					id: id,
					title: groupedEvents[0].title,
					numOfParts: eventsGroupedById[id].length,
					start: moment(groupedEvents[0].start),
					end: groupedEvents[groupedEvents.length - 1].end && moment(groupedEvents[groupedEvents.length - 1].end),
					editable: groupedEvents[0].editable,
					events: groupedEvents,
				};
				groupedEvents.forEach((event, i) => {
					event.complexEventConfig = complexEventConfig;
					event.id += '_' + i;
					event.complexEvent = true;
					event.complexEventPart = i;
					event.className = ['mx-complex-event'];
					event.title =
						this.i18n.getFormatted('solutionBuilderAgent.components.mx-calendar.complexEventTitle', event.complexEventPart + 1, event.complexEventConfig.numOfParts) +
						': ' +
						(event.title || '');

					event.editable = false;
					event.startEditable = false;
					event.durationEditable = false;
					event.tooltip = this.i18n.get('solutionBuilderAgent.components.mx-calendar.complexEventTooltip');
				});
				groupedEvents[0].startEditable = complexEventConfig.editable;
				groupedEvents[0].className.push('mx-complex-event-first');
				groupedEvents[complexEventConfig.numOfParts - 1].durationEditable = complexEventConfig.editable;
				groupedEvents[complexEventConfig.numOfParts - 1].className.push('mx-complex-event-last');
			}
		});
		return events;
	}

	mapHolidays(data) {
		if (Array.isArray(data)) {
			return data.map((holiday) => {
				const resourceIds = this.isTimeline ? [] : this.resources.map((resource) => resource.id);

				return {
					title: holiday.name,
					start: this.getWholeDayTimezoneCorrection(holiday.dateFrom),
					editable: false,
					allDay: true,
					holidayEvent: true,
					resourceIds: resourceIds,
					__originalItem: holiday,
				};
			});
		}
		return [];
	}

	async loadHolidays(start, end) {
		const data = await this.holidaysService.getUserHolidays(start.toISOString(), end.toISOString());
		return this.mapHolidays(data);
	}

	reloadResources() {
		if (!this.previousRangeStart) {
			return;
		}

		const resources = this.getResources(this.previousRangeStart, this.previousRangeEnd);
		const resourcesChanged = this.isResourcesChanged(this.resources, resources);
		if (resourcesChanged) {
			this.resources = resources;
			this._resourcePromise = this.$q.when(this.resources);

			if (this.calendar) {
				this.calendar.refetchResources();
			}
		}
	}

	getResources(start, end) {
		if (!this.isGroupingMode) {
			return [];
		}
		this.resourcesRendered = {};
		let resources = [];
		const dimensions = Array.isArray(this.extraDimensions) ? this.extraDimensions.slice() : [];

		if (this.isTimeline) {
			resources = dimensions.map((dimension) => {
				return {
					id: dimension.id,
					title: this.i18n.get('solutionBuilderAgent.components.mx-calendar.selectRangeToAddItem'),
					dimensionId: dimension.id,
					dimensionTitle: dimension.title,
					emptyLine: true,
				};
			});

			if (this.showUnassigned) {
				resources.unshift({
					id: unassignedId,
					title: this.i18n.get('solutionBuilderAgent.components.mx-calendar.selectRangeToAddItem'),
					dimensionId: null,
					dimensionTitle: this.unassignedTitle,
					emptyLine: true,
				});
			}

			const childrenResources = this.mapEvents(this.items, start, end)
				.map((event) => {
					const resource =
						resources.find(function (resource) {
							return resource.dimensionId === event.dimensionId;
						}) || {};
					return {
						id: event.complexEvent ? event.complexEventConfig.id : event.id,
						title: event.complexEvent ? event.complexEventConfig.title : event.title,
						dimensionTitle: resource.dimensionTitle,
						dimensionId: resource.dimensionId,
					};
				})
				.filter(function (resource) {
					return typeof resource.dimensionId !== 'undefined';
				});

			const accum = {};
			resources = childrenResources.concat(resources);
			resources.forEach(function (item) {
				if (!accum.hasOwnProperty(item.dimensionId)) {
					accum[item.dimensionId] = true;
				}
				item.isOdd = !accum[item.dimensionId];
				accum[item.dimensionId] = !accum[item.dimensionId];
			});
		} else {
			resources = dimensions;
			resources.forEach(function (resource) {
				resource.dimensionId = resource.id;
				resource.eventsOfDay = {};
			});

			if (this.showUnassigned) {
				resources.unshift({
					id: unassignedId,
					title: this.unassignedTitle,
					dimensionId: null,
					eventsOfDay: {},
				});
			}
		}

		return resources;
	}

	isResourcesChanged(oldResources, newResources) {
		if (!Array.isArray(oldResources) || !Array.isArray(newResources) || oldResources.length !== newResources.length) {
			return true;
		}
		const dimensions = {};
		oldResources.forEach(function (resource) {
			dimensions[resource.id] = resource.dimensionId;
		});
		return !newResources.every(function (resource) {
			return dimensions[resource.id] === resource.dimensionId;
		});
	}

	addBufferTime(start, end) {
		// TODO: Improve the showing events between the switching.
		// At current moment it was reached through the extension of the date range's window.
		let bufferSize = 0;
		switch (this.calendarView) {
			case 'day':
				bufferSize = 4;
				break;
			case 'week':
				bufferSize = 3;
				break;
			case 'month':
				bufferSize = 2;
				break;
		}
		start.add(-bufferSize, this.calendarView + 's');
		end.add(bufferSize, this.calendarView + 's');
	}

	onEventClick(item, clickEvent) {
		if (item.holidayEvent) {
			return;
		}

		if (
			(this.enableRemoving || '').toLowerCase() === 'true' &&
			// If clicked on close-button "X"
			clickEvent.offsetY <= 14 &&
			clickEvent.offsetX >= $(clickEvent.target).width() - 14
		) {
			this.$scope.$evalAsync(() => {
				if (item.__originalItem) {
					const index = this.items.indexOf(item.__originalItem);
					this.items.splice(index, 1);
					// updateEvents(vm.items);
					this.onItemRemoved({
						event: clickEvent,
						item: item.__originalItem,
					});
				}
			});
		} else {
			this.onItemClick({
				event: clickEvent,
				item: item.__originalItem,
			});
		}
	}

	onEventDestroy(event, element) {
		if (!this.dropdownMenuOptions) {
			return;
		}

		element.off('contextmenu');
	}

	rerenderCalendarHeader() {
		if (!this.calendar) {
			return;
		}
		this.calendar.renderHeader();
		this.calendar.setToolbarsTitle(this.calendar.view.title);
		this.calendar.updateToolbarButtons(this.calendar.view.dateProfile);
		this.calendar.toolbarsManager.proxyCall('activateButton', this.calendar.view.type);
	}

	updateTodayButtonTitle() {
		if (!this.calendar) {
			return;
		}
		this.calendar.optionsManager.overrides.buttonText = {
			today: this.getTodayButtonTitle(),
		};
		this.rerenderCalendarHeader();
	}

	viewRender(view /*, element*/) {
		this.view = view;
		this.calendar = view.calendar;
		this.resourcesRendered = {};

		if (this.calendarView !== view.viewSpec.singleUnit) {
			this.calendarView = view.viewSpec.singleUnit;
			this.personalizationService.saveCalendarView(this.contextId, this.calendarView);

			this.updateTodayButtonTitle();
		}
		if (this.autoWidth && !this.isTimeline) {
			const columnMinWidth = this.calendarView !== 'month' ? 245 : 100;
			const numOfColumns = (this.calendarView === 'day' ? 1 : 7) * Math.max(this.resources.length, 1);
			this.calendarWidth = numOfColumns * columnMinWidth + 'px';
		} else {
			this.calendarWidth = 'auto';
		}

		// restore moment correct locale.
		moment.locale(mx.components.Utils.getMomentJsSupportedLocale(this.language));
	}

	eventRender(event, element) {
		const content = element[0].firstChild;
		let node;

		if (event.description) {
			node = document.createElement('div');
			node.className = 'fc-description';
			node.innerText = event.description;
			content.appendChild(node);
		}

		if (event.tooltip) {
			element.attr('title', event.tooltip);
		}

		if (!this.dropdownMenuOptions) {
			return;
		}

		element.on('contextmenu', async (ev) => {
			ev.preventDefault();

			if (!event.holidayEvent && !event.isBackgroundItem) {
				const actions = await this.onItemRightClick({ item: event.__originalItem });
				angular.extend(this.dropdownMenuOptions, {
					dropdownEvent: ev,
					menuItems: actions,
					isDropdownOpen: true,
				});
			}
		});
	}

	resourceRender(resource, labelTds, bodyTds, view) {
		this.view = view;
		this.calendar = view.calendar;

		if (!this.isGroupingMode || (!this.isTimeline && view.viewSpec.singleUnit === 'month')) {
			return;
		}

		if (this.isTimeline) {
			if (resource.emptyLine) {
				labelTds.addClass('resource-empty-line');
				bodyTds.addClass('resource-empty-line');
			}
			const buildClassName = (type) => 'resource-item-base-' + type + (resource.isOdd ? '-odd' : '-even');
			labelTds.addClass(buildClassName('label'));
			bodyTds.addClass(buildClassName('body'));
			return;
		}

		this.resourcesRendered[resource.id] = this.resourcesRendered[resource.id] || 0;

		labelTds[0].innerHTML = '';

		const dayNumber = this.resourcesRendered[resource.id];

		const template = '<mx-calendar-resource ' + 'day-number="' + dayNumber + '" ' + 'resource-id="' + resource.id + '" ' + ' />';

		const _elem = angular.element(template);
		$(labelTds[0]).append(_elem);
		this.compile(_elem);

		this.resourcesRendered[resource.id]++;
	}

	setNumberOfEventsPerDay() {
		if (this.isGroupingMode && !this.isTimeline && this.view && this.view.timeGrid) {
			this.view.timeGrid.dayIndices.forEach((dayNumber) => {
				this.resources.forEach((resource) => {
					resource.eventsOfDay[dayNumber] = this.getNumEventsOfDay(dayNumber, resource.id);
				});
			});
		}
	}

	updateNumberOfEventsPerDay(newEvent, oldEvent) {
		const newDayNumber = this.getDayNumberByTime(newEvent.start, newEvent.end);
		const oldDayNumber = this.getDayNumberByTime(oldEvent.start, oldEvent.end);
		const resource = this.resources.find((resource) => resource.id === newEvent.resourceId);
		const isResourceChanged = newEvent.resourceId !== oldEvent.resourceId;

		if (isResourceChanged) {
			const newResource = resource;
			const oldResource = this.resources.find((resource) => resource.id === oldEvent.resourceId);

			newResource.eventsOfDay[newDayNumber]++;
			oldResource.eventsOfDay[oldDayNumber]--;
		} else {
			resource.eventsOfDay[newDayNumber]++;
			resource.eventsOfDay[oldDayNumber]--;
		}
	}

	onAfterAllRendered(view) {
		this.setNumberOfEventsPerDay();

		if (view.axisWidth > 1) {
			$('th.fc-axis.fc-widget-header').css('min-width', view.axisWidth + 'px');
		}

		// in autoWidth mode in "week" view calendar width can be more than container width,
		// so it is necessary to scroll to current day after init,
		// but only if calendar is visible at current moment (ex. it should not be scrolled if it was rendered in hidden tab)
		if (this.firstLoad && this.autoWidth && this.calendarView === 'week' && this.$element.is(':visible') && this.$element.css('visibility') !== 'hidden') {
			this.scrollToCurrentDay();
		}

		this.firstLoad = false;
	}

	getTodayButtonTitle() {
		switch (this.calendarView) {
			case 'week':
				return this.i18n.get('solutionBuilderAgent.components.mx-calendar.currentWeek');
			case 'month':
				return this.i18n.get('solutionBuilderAgent.components.mx-calendar.currentMonth');
			case 'day':
				return this.i18n.get('solutionBuilderAgent.common.today');
			default:
				return this.i18n.get('solutionBuilderAgent.common.today');
		}
	}

	setCalendarView(inputView) {
		const supportedViews = this.getSupportedViews();

		switch (inputView) {
			case 'day':
			case 'agendaDay':
				this.calendarView = 'day';
				break;
			case 'week':
			case 'agendaWeek':
				this.calendarView = 'week';
				break;
			case 'month':
				this.calendarView = 'month';
				break;
			default:
				this.calendarView = 'week';
				break;
		}

		if (supportedViews.indexOf(this.calendarView) < 0) {
			if (!supportedViews.length || supportedViews.indexOf(defaultCalendarView) > 0) {
				this.calendarView = defaultCalendarView;
			} else {
				this.calendarView = supportedViews[0];
			}
		}
	}

	getDefaultView() {
		return viewNames[this.getCurrentViewType()][this.calendarView];
	}

	enableAbstractMode() {
		angular.extend(this.options, {
			header: {
				left: '',
				center: '',
				right: '',
			},
			columnFormat: 'ddd',
			defaultDate: new Date('1970-01-05'),
			timezone: 'UTC',
		});
	}

	getSupportedViews() {
		const supportedViews = [];
		['Day', 'Week', 'Month'].forEach((viewName) => {
			if (this['show' + viewName + 'View'] !== false) {
				//true or undefined (by default)
				supportedViews.push(viewName.toLowerCase());
			}
		});
		return supportedViews;
	}

	getCurrentViewType() {
		return this.isTimeline ? 'timeline' : 'agenda';
	}

	updateRangeButtons() {
		this.options.header.right = 'prev,today,next ';

		const viewType = this.getCurrentViewType();
		let views = '';
		const supportedViews = this.getSupportedViews();
		if (supportedViews.length <= 1) {
			return;
		}
		supportedViews.forEach((viewName) => {
			const view = viewNames[viewType][viewName] || '';
			views += (view && views ? ',' : '') + view;
		});
		this.options.header.right += views;
	}

	enableTimeline() {
		this.updateRangeButtons();
		this.options.slotLabelFormat = null;
		this.options.selectAllow = (selectInfo) => {
			return this.resources.some(
				(resource) => resource.id === selectInfo.resourceId && (resource.dimensionId === selectInfo.resourceId || selectInfo.resourceId === unassignedId)
			);
		};
	}

	disableTimeline() {
		this.updateRangeButtons();
		this.options.slotLabelFormat = 'LT';
		delete this.options.selectAllow;
	}

	enableGroupingMode() {
		angular.extend(this.options, {
			// datesAboveResources: true,
			views: {
				month: {
					groupByDateAndResource: false,
				},
			},
			slotLabelFormat: 'LT',
			nowIndicator: true,
			groupByDateAndResource: true,
			resourceGroupField: 'dimensionTitle',
			refetchResourcesOnNavigate: true,
			lazyFetching: false,
			resources: async (callback) => {
				const resources = await this._resourcePromise;
				callback(resources);
			},
			resourceRender: this.resourceRender.bind(this),
		});
	}

	getNumEventsOfDay(dayNumber, resourceId) {
		const dayRange = this.view.timeGrid.dayRanges[dayNumber];
		if (dayRange) {
			const start = moment(dayRange.startMs - moment().utcOffset() * 60000),
				end = moment(dayRange.endMs - moment().utcOffset() * 60000);
			const comparer = this.rangeComparer(start.toDate(), end.toDate());
			const events = this.calendar.getResourceEvents(resourceId);
			return events.filter((event) => !event.isBackgroundItem && !event.holidayEvent && comparer(event)).length;
		}
	}

	compile(tpl /*, resource*/) {
		let localScope = this.$rootScope.$new(false, this.$scope);
		this.$compile(tpl)(localScope);
		this.$element.on('$destroy', () => {
			if (localScope) {
				localScope.$destroy();

				localScope = null;
			}
		});
	}

	_doesEventOverlapAnyEvents(item) {
		const itemStart = moment.utc(item[this.itemStartKey]);
		const itemEnd = moment.utc(item[this.itemEndKey]);
		//TODO: works just for 1 case, fix for all cases
		return this.events.some((it) => itemStart < it.end && it.start < itemEnd);
	}

	createEvent(newItem) {
		this.disabled = true;

		return (
			typeof this.onRangeSelect === 'function'
				? // External Handler
				  this.onRangeSelect({
						start: newItem[this.itemStartKey],
						end: newItem[this.itemEndKey],
						wholeDay: newItem[this.itemWholeDayKey],
						dimensionId: this.getExtraDimensionIdFromItem(newItem),
				  })
				: // Internal handler
				  this.$q((resolve) => {
						this.items.push(newItem);
						resolve(newItem);
				  })
		).finally(() => {
			this.setNumberOfEventsPerDay();
			this.disabled = false;
		});
	}

	getDayNumberByTime(start, end) {
		const day = this.view.timeGrid.dayRanges.find(function (range) {
			const dayStart = moment(range.startMs);
			const dayEnd = moment(range.endMs);
			return start >= dayStart && end <= dayEnd;
		});
		return this.view.timeGrid.dayRanges.indexOf(day);
	}

	async _handleItemPositionChange(event, delta, revertFunc /*, jsEvent, ui, view*/) {
		this.disabled = true;
		const oldEvent = this.mapEvent(event.__originalItem);
		oldEvent.start = moment(oldEvent.start);
		oldEvent.end = oldEvent.end && moment(oldEvent.end);

		if (this.isGroupingMode) {
			event.dimensionId = this.resources.find((resource) => resource.id === event.resourceId).dimensionId;
		}

		let start, end;

		if (event.complexEvent) {
			start = moment(event.complexEventConfig.start + (event.start - oldEvent.start));
			if (event.end) {
				end = moment(event.complexEventConfig.end + (event.end - oldEvent.end));
			}
		} else {
			start = event.start;
			end = event.end;
		}

		try {
			//external handler
			if (typeof this.onItemMove === 'function') {
				await this.onItemMove({
					start: this.getLocalTimeISOString(start),
					end: end && this.getLocalTimeISOString(end),
					wholeDay: event.allDay,
					dimensionId: event.dimensionId,
					item: event.__originalItem,
				});
			}

			this.updateChangedEvent(event);
			if (this.isGroupingMode) {
				this.updateNumberOfEventsPerDay(event, oldEvent);
			}
		} catch (e) {
			this.reset = revertFunc;
			this.reset();
		}

		this.disabled = false;
	}

	getResourceIdFromItem(item) {
		if (!this.isGroupingMode) {
			return;
		}

		if (this.isTimeline) {
			return item._id;
		} else {
			const dimensionId = this.getExtraDimensionIdFromItem(item);
			return dimensionId === null ? unassignedId : dimensionId;
		}
	}

	getExtraDimensionIdFromItem(item) {
		return this.isGroupingMode && typeof item[this.itemExtraDimensionKey] !== 'undefined' ? item[this.itemExtraDimensionKey] : null;
	}

	updateChangedEvent(event) {
		event.__originalItem[this.itemStartKey] = this.getLocalTimeISOString(event.start);
		event.__originalItem[this.itemEndKey] = event.end && this.getLocalTimeISOString(event.end);
		event.__originalItem[this.itemWholeDayKey] = event.allDay;
		if (this.isGroupingMode) {
			event.__originalItem[this.itemExtraDimensionKey] = event.dimensionId;
		}
	}

	scrollToCurrentDay() {
		const element = $('.fc-view-container .fc-view .fc-head .fc-day-header.fc-today')[0];
		if (!element) {
			return;
		}

		if (typeof element.scrollIntoView === 'function') {
			element.scrollIntoView({
				block: 'end',
				inline: 'center',
			});
			// Fix Calendar layout for Edge (after page refresh by browser button)
			if (/Edge/.test(navigator.userAgent)) {
				// force layout refresh to fix it
				$(window).trigger('resize');
			}
		}
	}

	async initEventColor() {
		const currentTheme = await this.themeService.getCurrentTheme();
		const warningColor = currentTheme['validation-warning-color'];
		const contentBgColor = currentTheme['content-bg-color'];
		const contentTextColor = currentTheme['content-text-color'];

		const colorService = this.colorService;
		const colorContrastDelta = colorService.getContrastDelta(warningColor, contentBgColor);

		const newContrastDelta = colorService.getContrastDelta(colorService.mixColors(contentTextColor, contentBgColor, 0.5), contentBgColor);

		const getAverageLightness = (a, b) => (colorService.getLightness(a) + colorService.getLightness(b)) / 2;
		const eventColor =
			colorContrastDelta < 50 && newContrastDelta > colorContrastDelta
				? this.colorService.lighten(warningColor, getAverageLightness(contentTextColor, contentBgColor))
				: warningColor;

		const eventTextColor = this.colorService.getProperContrastTextColor(eventColor);
		const eventColorContrast = this.colorService.enhanceColorContrast(eventColor, eventTextColor, 7);

		this.$element.css('--mx-calendar-event-color', eventColor);
		this.$element.css('--mx-calendar-event-text-color', eventTextColor);
		this.$element.css('--mx-calendar-event-color-contrast', eventColorContrast);
	}
}
CalendarController.$inject = [
	'$scope',
	'$q',
	'$timeout',
	'$ocLazyLoad',
	'mx.components.LazyLoadCfg',
	'$element',
	'$rootScope',
	'$compile',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.HolidaysService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.app.ColorService',
	'mx.app.ThemeService',
];

// jQuery 3.5 has breaking change which corrupt angular-ui-calendar.
// To fix it, according to JQuery 3.5 Upgrade Guide https://jquery.com/upgrade-guide/3.5/,
// it is added workaround for htmlPrefilter
// TODO: Should be removed when BUG 263731 is fixed
// https://matrix42.visualstudio.com/BPM/_backlogs/backlog/KERBEROS/Backlog%20items/?workitem=263731
const originalHtmlPrefilter = $.htmlPrefilter;
const hackedPrefilter = function (html) {
	const rxHtmlTag = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([a-z][^/\0>\x20\t\r\n\f]*)[^>]*)\/>/gi;
	return html.replace(rxHtmlTag, '<$1></$2>');
};

let openedCalendars = 0;

function enableJQueryHtmlPrefilterHack() {
	if (openedCalendars === 0) {
		$.htmlPrefilter = hackedPrefilter;
	}
	openedCalendars++;
}

function disableJQueryHtmlPrefilterHack() {
	openedCalendars--;
	if (openedCalendars === 0) {
		$.htmlPrefilter = originalHtmlPrefilter;
	}
}
